import { Component, isDevMode, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';

import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { each, cloneDeep, has, orderBy, isEqual } from 'lodash';

import { Sector, SectorRole, SECTORS_PATHS } from '../sectors';
import { ConfirmModalComponent } from '../../../shared/modals/confirm-modal/confirm-modal.component';
import { SectorsService } from '../sectors.service';
import { PaginationOnLoad } from '../../../shared/pagination/pagination';
import { PaginationService } from '../../../shared/pagination/pagination.service';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { Breadcrumb } from '../../../shared/breadcrumbs/breadcrumbs';
import { Enums } from '../../../configs/Enums';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'app-plots',
  templateUrl: './sectors.component.html',
  styleUrls: ['./sectors.component.scss'],
  standalone: false,
})
export class SectorsComponent implements OnInit {
  public filter = {
    sectorsFilter: null,
    sectorsSelected: null,
    sectorExpanded: null,
    branchesSelected: null,
    citiesSelected: null,
    districtsSelected: null,
    companiesSelected: null,
    sectorRoleSelected: null,
    regionsSelected: null,
    isVisibleHiddenSectors: false,
    onChangeBranches: this.onChangeBranches.bind(this),
    onChangeSectorRole: this.onChangeSectorRole.bind(this),
    onChangeCities: this.onChangeCities.bind(this),
    onChangeCompanies: this.onChangeCompanies.bind(this),
    onChangeDistricts: this.onChangeDistricts.bind(this),
    onChangeSectors: this.onChangeSectors.bind(this),
    onApplyPlotsFilter: this.onApplyPlotsFilter.bind(this),
    onResetPlotsFilter: this.onResetPlotsFilter.bind(this),
    onChangeRegions: this.onChangeRegions.bind(this),
    onChangeVisibleHiddenPlots: this.onChangeVisibleHiddenPlots.bind(this),
    onChangeSorting: this.onChangeSorting.bind(this),
    sortColumn: null,
    sortDirection: null,
  };
  public data = {
    branchesList: null,
    citiesList: null,
    districtsList: null,
    sectorsList: null,
    sectors: null,
    companiesList: null,
    regionsList: null,
  };
  public tableBlock = {
    sectorsLoading: false,
    onLoadPartial: this.onLoadPartial.bind(this),
    onChangePlotExpanded: this.onChangePlotExpanded.bind(this),
    onChangeStatePlot: this.onChangeStatePlot.bind(this),
    emptyMessage: null,
  };
  public SECTORS_PATHS = SECTORS_PATHS;
  public isExpandedDataLoaded: boolean;
  public stateParams = {
    sectors_ids: '',
    branches_ids: '',
    cities_ids: '',
    districts_ids: '',
    partnerships_ids: '',
    mb_regions: '',
    visible_only: 0,
    sector_role: ''
  };
  public stateParamsDefault = cloneDeep(this.stateParams);
  public paginationBlock = {
    limits: [25, 50, 100],
    limitSelected: 25,
    limitCountMaxRows: 0,
    offset: 0,
    countLoaded: 0,
  };
  public editSectorModal: NgbModalRef;

  public plotsBreadcrumbs: Breadcrumb[] = [{ text: 'Участки', url: null }];

  constructor(
    private breadcrumbsService: BreadcrumbsService,
    private paginationService: PaginationService,
    public modalService: NgbModal,
    public sectorsService: SectorsService,
    public router: Router,
    public route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.breadcrumbsService.updateBreadcrumbs(this.plotsBreadcrumbs);
    this.subscribeChangeParams();
  }

  public subscribeChangeParams(): void {
    this.route.params.subscribe((params) => {
      const stateParams = this.stateParams;
      let countParameters = 0;
      let countExistsParameters = 0;

      stateParams.sectors_ids = params['sectors_ids'] || '';
      stateParams.branches_ids = params['branches_ids'] || '';
      stateParams.cities_ids = params['cities_ids'] || '';
      stateParams.districts_ids = params['districts_ids'] || '';
      stateParams.partnerships_ids = params['partnerships_ids'] || '';
      stateParams.mb_regions = params['mb_regions'] || '';
      stateParams.visible_only = +params['visible_only'] || 0;
      stateParams.sector_role = params['sector_role'] || '';

      each(stateParams, function (value, key) {
        countParameters++;

        if (has(params, [key])) {
          countExistsParameters++;
        }
      });

      this.paginationService.initPagination(this.paginationBlock);

      if (countParameters === countExistsParameters) {
        this.getSectorsFilterData();
      } else {
        this.router.navigate([SECTORS_PATHS.sectors, stateParams], {
          replaceUrl: true,
        });
      }
    });
  }

  public createSector(): void {
    this.router.navigate(['/' + SECTORS_PATHS.sectors_create]);
  }

  private makeSectorsParams(): any {
    const filter = this.filter;

    return {
      sectors_ids: (filter.sectorsSelected && filter.sectorsSelected.map((s) => s.sector_id)) || '',
      branches_ids: (filter.branchesSelected && filter.branchesSelected.map((b) => b.mb_id)) || '',
      cities_ids: (filter.citiesSelected && filter.citiesSelected.map((c) => c.ct_id)) || '',
      districts_ids: (filter.districtsSelected && filter.districtsSelected.map((d) => d.d_id)) || '',
      partnerships_ids: (filter.companiesSelected && filter.companiesSelected.map((c) => c.campaign_id)) || '',
      mb_regions: (filter.regionsSelected && filter.regionsSelected.map((r) => r.name)) || '',
      sector_role: filter.sectorRoleSelected || '',
      visible_only: !filter.isVisibleHiddenSectors,
    };
  }

  private getSectorsFilterData(): void {
    const params = cloneDeep(this.stateParams);
    each(params, (value, key) => {
      params[key] = value && typeof value === 'string' ? value.split(',') : '';
    });
    delete params.visible_only;

    this.isExpandedDataLoaded = true;
    this.tableBlock.sectorsLoading = true;
    this.sectorsService.getFilterData(params).subscribe(
      (response) => {
        let resp = response;
        let data = this.data;

        data.citiesList = resp[0]['data'];
        data.branchesList = resp[1]['data'];
        data.sectorsList = this.sectorsService.makeSectorsNumbers(resp[2]['data']);
        data.companiesList = resp[3]['data'].map((company) => {
          company.new_name = company.ownership_type.ot_short + ' "' + company.ps_name + '"';
          return company;
        });
        data.regionsList = resp[4]['data'].map((name) => {
          return { name: name };
        });
        data.districtsList = resp[5]['data'].map((d) => {
          d.new_name = d.city.ct_name + ' - ' + d.district_name;
          return d;
        });
        this.initFilterData();
        this.getSectors(this.paginationBlock.limitSelected, this.paginationBlock.offset);
      },
      (error) => {
        console.log(error);
        this.tableBlock.sectorsLoading = false;
      }
    );
  }

  private getSectors(limit: number, offset: number): void {
    const filter = this.filter;
    const requestQueryParams = this.makeSectorsParams();
    requestQueryParams.offset = offset;
    requestQueryParams.limit = limit;
    requestQueryParams.visible_only = +requestQueryParams.visible_only;

    if (filter.sortDirection && filter.sortColumn) {
      requestQueryParams.sort_column = filter.sortColumn;
      requestQueryParams.sort_direction = filter.sortDirection;
    }

    this.tableBlock.sectorsLoading = true;

    this.sectorsService.getPlots(requestQueryParams).subscribe(
      (response) => {
        let result = response['data']['results'];

        if (offset > 0) {
          this.data.sectors = this.data.sectors.concat(result);
        } else {
          if (!this.data.sectors) {
            this.data.sectors = [];
          }

          this.data.sectors = result;
        }

        this.paginationService.updatePaginationLoadPartial(this.data.sectors.length, response['data']['count']);
        this.tableBlock.sectorsLoading = false;
      },
      (error) => {
        this.tableBlock.sectorsLoading = false;
      }
    );
  }

  getCities(): void {
    const filter = this.filter;
    const params: any = {};

    if (filter.regionsSelected) {
      params.mb_regions = filter.regionsSelected.map((region) => region.name);
    }

    if (filter.branchesSelected) {
      params.mb_ids = this.filter.branchesSelected.map((b) => b.mb_id);
    }

    this.sectorsService.getCities(params).subscribe(
      (response) => {
        this.data.citiesList = response['data'];
      },
      (error) => {
        this.data.citiesList = null;
      }
    );
  }

  getSectorsNames(): void {
    const filter = this.filter;
    const params: any = {};

    if (filter.regionsSelected) {
      params.mb_regions = filter.regionsSelected.map((region) => region.name);
    }

    if (filter.branchesSelected) {
      params.mb_ids = filter.branchesSelected.map((b) => b.mb_id);
    }

    if (filter.citiesSelected) {
      params.cities_ids = filter.citiesSelected.map((c) => c.ct_id);
    }

    if (filter.companiesSelected) {
      params.partnerships_ids = filter.companiesSelected.map((c) => c.partnership_id || c.campaign_id);
    }

    this.sectorsService.getFilterPlots(params).subscribe(
      (response) => {
        this.data.sectorsList = this.sectorsService.makeSectorsNumbers(response['data']);
      },
      (error) => {
        this.data.citiesList = null;
      }
    );
  }

  getDistricts(): void {
    const filter = this.filter;
    const params: any = {};

    if (filter.regionsSelected) {
      params.mb_regions = filter.regionsSelected.map((region) => region.name);
    }

    if (filter.citiesSelected) {
      params.cities_ids = filter.citiesSelected.map((c) => c.ct_id);
    }

    if (filter.branchesSelected) {
      params.mb_ids = this.filter.branchesSelected.map((b) => b.mb_id);
    }

    this.sectorsService.getDistricts(params).subscribe(
      (response) => {
        this.data.districtsList = response['data'].map((d) => {
          d.new_name = d.city.ct_name + ' - ' + d.district_name;
          return d;
        });
      },
      (error) => {
        console.log(error);
      }
    );
  }

  getBranches(): void {
    const filter = this.filter;

    if (!filter.regionsSelected) {
      return;
    }

    this.sectorsService
      .getBranches({
        mb_regions: filter.regionsSelected.map((region) => region.name),
      })
      .subscribe(
        (response) => {
          this.data.branchesList = response.data;
        },
        (error) => {}
      );
  }

  private initFilterData(): void {
    const filter = this.filter;
    const data = this.data;

    this.initEssence(filter, data, 'partnerships_ids', 'companiesList', 'campaign_id', 'companiesSelected');
    this.initEssence(filter, data, 'branches_ids', 'branchesList', 'mb_id', 'branchesSelected');
    this.initEssence(filter, data, 'cities_ids', 'citiesList', 'ct_id', 'citiesSelected');
    this.initEssence(filter, data, 'districts_ids', 'districtsList', 'd_id', 'districtsSelected');
    this.initEssence(filter, data, 'sectors_ids', 'sectorsList', 'sector_id', 'sectorsSelected');
    this.initEssence(filter, data, 'mb_regions', 'regionsList', 'name', 'regionsSelected');
    filter.isVisibleHiddenSectors = !!this.stateParams.visible_only;
  }

  private initEssence(filter, data, stateKey, dataKey, itemKey, selectedKey): void {
    if (this.stateParams[stateKey]) {
      const arr = this.stateParams[stateKey].split(',');
      const items = [];

      each(data[dataKey], (i) => {
        if (arr.indexOf(i[itemKey] + '') > -1) {
          items.push(i);
        }
      });

      filter[selectedKey] = items;
    } else {
      filter[selectedKey] = null;
    }
  }

  public onChangeCompanies(companies): void {
    const filter = this.filter;

    filter.companiesSelected = companies;
    filter.sectorsSelected = null;

    this.getSectorsNames();
  }

  public onChangeBranches(branches): void {
    const filter = this.filter;
    const data = this.data;

    filter.branchesSelected = branches;
    filter.citiesSelected = null;
    filter.districtsSelected = null;
    filter.sectorsSelected = null;
    data.citiesList = null;
    data.districtsList = null;
    data.sectorsList = null;

    this.getCities();
    this.getDistricts();
    this.getSectorsNames();
  }

  public onChangeSectorRole(sectorRole: SectorRole): void {
    this.filter.sectorRoleSelected = sectorRole;
  }

  public onChangeCities(cities): void {
    const filter = this.filter;
    const data = this.data;

    filter.citiesSelected = cities;

    filter.districtsSelected = null;
    filter.sectorsSelected = null;
    data.districtsList = null;

    this.getDistricts();
    this.getSectorsNames();
  }

  public onChangeDistricts(districts): void {
    this.filter.districtsSelected = districts;
  }

  public onChangeSectors(sectors): void {
    this.filter.sectorsSelected = sectors;
  }

  public onChangeRegions(regions): void {
    const filter = this.filter;
    const data = this.data;

    this.filter.regionsSelected = regions;
    filter.branchesSelected = null;
    filter.citiesSelected = null;
    filter.districtsSelected = null;
    filter.sectorsSelected = null;
    data.branchesList = null;
    data.citiesList = null;
    data.districtsList = null;

    this.getBranches();
    this.getCities();
    this.getDistricts();
    this.getSectorsNames();
  }

  onApplyPlotsFilter(): void {
    if (isDevMode()) {
      console.log('APPLY FILTER ~~~~~~~~~~~~', this);
    }

    let oldParams = cloneDeep(this.stateParams);
    let params = cloneDeep(this.makeSectorsParams());
    params.companies_ids = (params.companies_ids && params.companies_ids.join(',')) || '';
    params.branches_ids = (params.branches_ids && params.branches_ids.join(',')) || '';
    params.cities_ids = (params.cities_ids && params.cities_ids.join(',')) || '';
    params.districts_ids = (params.districts_ids && params.districts_ids.join(',')) || '';
    params.sectors_ids = (params.sectors_ids && params.sectors_ids.join(',')) || '';
    params.sector_role = (params.sector_role && params.sector_role) || '';
    params.mb_regions = (params.mb_regions && params.mb_regions.join(',')) || '';
    params.visible_only = +!params.visible_only;

    if (isEqual(oldParams, params)) {
      if (isDevMode()) {
        console.log('~~~~ПАРАМЕТРЫ НЕ ОТЛИЧАЮТСЯ, НУЖНО ПУЛУЧАТЬ ДАННЫЕ~~~~');
      }

      this.getSectors(this.paginationBlock.limitSelected, this.paginationBlock.offset);
    } else {
      if (isDevMode()) {
        console.log('~~~~ПАРАМЕТРЫ ОТЛИЧАЮТСЯ, НУЖНО СДЕЛАТЬ РЕДИРЕКТ~~~~');
      }

      this.paginationService.updatePaginationLoadPartial(0, 0);
      this.router.navigate([SECTORS_PATHS.sectors, params]);
    }
  }

  onResetPlotsFilter(): void {
    this.stateParams = cloneDeep(this.stateParamsDefault);
    this.filter.sectorExpanded = null;
    this.paginationService.updatePaginationLoadPartial(0, 0);
    this.router.navigate([SECTORS_PATHS.sectors, this.stateParams]);
  }

  onChangeVisibleHiddenPlots(event): void {
    this.paginationService.updatePaginationLoadPartial(0, 0);
    this.stateParams.visible_only = +event.target.checked;
    this.router.navigate([SECTORS_PATHS.sectors, this.stateParams]);
  }

  onChangePlotExpanded(sector): void {
    const filter = this.filter;

    if (sector.state === 'open') {
      filter.sectorExpanded = sector.sector;
    } else {
      filter.sectorExpanded = null;
    }
  }

  onChangeStatePlot(sector: Sector): void {
    if (sector.is_visible) {
      this.onOpenPlotConfirmDialog(sector);
    } else {
      this.changeStatePlot({ sector: sector }).subscribe(() => {
        this.getSectors(this.paginationBlock.limitSelected, this.paginationBlock.offset);
      });
    }
  }

  onOpenPlotConfirmDialog(sector: Sector): void {
    this.editSectorModal = this.modalService.open(ConfirmModalComponent, {
      ariaLabelledBy: 'modal-basic-title',
      windowClass: 'modal-wrapper',
      backdrop: 'static',
    });

    this.editSectorModal.componentInstance.title = 'Подтвердите отключение';
    this.editSectorModal.componentInstance.description =
      'Участок ' +
      sector.sector_number_str +
      ' будет скрыт. ' +
      'Вы не сможете использовать его при регистрации / редактировании нового сотрудника. Вы действительно' +
      ' хотите скрыть эту запись?';
    this.editSectorModal.componentInstance.successButtonName = 'Да, отключить';
    this.editSectorModal.componentInstance.abortButtonName = 'Отмена';
    this.editSectorModal.componentInstance.data = { sector: sector };
    this.editSectorModal.componentInstance.successCallbackAction = this.changeStatePlot.bind(this);
    this.editSectorModal.componentInstance.errorMessageCode = Enums.errorCodesMaps['BACKEND_ERROR'];
    this.editSectorModal.result.then(
      () => {
        this.getSectors(this.paginationBlock.limitSelected, 0);
      },
      () => {}
    );
  }

  changeStatePlot(data) {
    return this.sectorsService.updatePlotStatus(data.sector.s_id, {
      is_visible: +!data.sector.is_visible,
    });
  }

  onLoadPartial(paginationLoad: PaginationOnLoad): void {
    this.getSectors(paginationLoad.limit, paginationLoad.offset);
  }

  private sortData(): void {
    const { sortColumn, sortDirection } = this.filter;
      if (sortColumn && sortDirection) {
        this.data.sectors = orderBy(
          this.data.sectors,
          [sortColumn],
          [sortDirection] 
        );
      }
  }

  onChangeSorting(sortData): void {
    const filter = this.filter;

    filter.sortColumn = sortData.sortColumn;
    filter.sortDirection = sortData.sortDirection;
    this.sortData()
  }
}
